
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { 
  Table, 
  TableBody, 
  TableCaption, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";

interface PersonalityTest {
  id: string;
  user_id: string;
  personality_type: string;
  created_at: string;
}

interface SavedCareer {
  id: string;
  user_id: string;
  career_id: string;
  career_title: string;
  created_at: string;
}

export function UserActivityTable() {
  const [tests, setTests] = useState<PersonalityTest[]>([]);
  const [savedCareers, setSavedCareers] = useState<SavedCareer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("tests");
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch personality tests
        const { data: testsData, error: testsError } = await supabase
          .from('personality_tests')
          .select('*');
          
        if (testsError) {
          throw testsError;
        }
        
        setTests(testsData || []);
        
        // Fetch saved careers
        const { data: careersData, error: careersError } = await supabase
          .from('saved_careers')
          .select('*');
          
        if (careersError) {
          throw careersError;
        }
        
        setSavedCareers(careersData || []);
        
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please check your permissions.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  return (
    <Tabs defaultValue="tests" onValueChange={setActiveTab}>
      <TabsList className="mb-4">
        <TabsTrigger value="tests">Personality Tests</TabsTrigger>
        <TabsTrigger value="careers">Saved Careers</TabsTrigger>
      </TabsList>
      
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : error ? (
        <div className="text-destructive text-center py-4">{error}</div>
      ) : (
        <>
          <TabsContent value="tests">
            <Table>
              <TableCaption>List of all personality tests</TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead>User ID</TableHead>
                  <TableHead>Personality Type</TableHead>
                  <TableHead>Test Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center">No tests found</TableCell>
                  </TableRow>
                ) : (
                  tests.map((test) => (
                    <TableRow key={test.id}>
                      <TableCell className="font-mono text-xs">
                        {test.user_id}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{test.personality_type}</Badge>
                      </TableCell>
                      <TableCell>
                        {test.created_at 
                          ? new Date(test.created_at).toLocaleDateString() 
                          : 'N/A'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TabsContent>
          
          <TabsContent value="careers">
            <Table>
              <TableCaption>List of all saved careers</TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead>User ID</TableHead>
                  <TableHead>Career Title</TableHead>
                  <TableHead>Saved Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {savedCareers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center">No saved careers found</TableCell>
                  </TableRow>
                ) : (
                  savedCareers.map((career) => (
                    <TableRow key={career.id}>
                      <TableCell className="font-mono text-xs">
                        {career.user_id}
                      </TableCell>
                      <TableCell>{career.career_title}</TableCell>
                      <TableCell>
                        {career.created_at 
                          ? new Date(career.created_at).toLocaleDateString() 
                          : 'N/A'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TabsContent>
        </>
      )}
    </Tabs>
  );
}
